<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'next/button/Button.vue';
import { BaseTable, BaseTableRow, BaseTableCell } from 'next/table';
import { messageStamp } from 'shared/helpers/timeHelper';
import DeleteCustomDashboardDialog from './delete/DeleteCustomDashboardDialog.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const deleteDialogRef = ref(null);

const dashboards = useMapGetter('wgptCustomDashboards/getCustomDashboards');
const uiFlags = useMapGetter('wgptCustomDashboards/getUIFlags');

const tableHeaders = computed(() => [
  t('WGPT_CUSTOM_DASHBOARDS.LIST.TABLE_HEADER.NAME'),
  t('WGPT_CUSTOM_DASHBOARDS.LIST.TABLE_HEADER.WIDGETS'),
  t('WGPT_CUSTOM_DASHBOARDS.LIST.UPDATED_ON'),
]);

onMounted(() => {
  store.dispatch('wgptCustomDashboards/get');
});

function navigateToEdit(dashboard) {
  router.push(
    accountScopedRoute('wgpt_settings_custom_dashboards_edit', {
      dashboardId: dashboard.id,
    })
  );
}

const readableDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy hh:mm a');
</script>

<template>
  <div>
    <woot-loading-state v-if="uiFlags.isFetching" />

    <p
      v-else-if="!dashboards.length"
      class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
    >
      {{ t('WGPT_CUSTOM_DASHBOARDS.EMPTY_STATE.TITLE') }}
    </p>

    <BaseTable v-else :headers="tableHeaders" :items="dashboards">
      <template #row="{ items }">
        <BaseTableRow
          v-for="dashboard in items"
          :key="dashboard.id"
          :item="dashboard"
        >
          <template #default>
            <BaseTableCell class="max-w-0 w-full">
              <div class="flex flex-col items-start gap-1">
                <span class="block text-heading-3 text-n-slate-12">
                  {{ dashboard.name }}
                </span>
                <p
                  v-if="dashboard.description"
                  class="mb-0 text-n-slate-11 text-body-main truncate max-w-xs"
                >
                  {{ dashboard.description }}
                </p>
              </div>
            </BaseTableCell>

            <BaseTableCell>
              <span class="text-body-main text-n-slate-11 whitespace-nowrap">
                {{
                  t('WGPT_CUSTOM_DASHBOARDS.LIST.WIDGET_COUNT', {
                    count: (dashboard.widgets || []).length,
                  })
                }}
              </span>
            </BaseTableCell>

            <BaseTableCell :title="readableDateWithTime(dashboard.updated_at)">
              <span class="text-body-main text-n-slate-12 whitespace-nowrap">
                {{ readableDate(dashboard.updated_at) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="end">
              <div class="flex gap-3 justify-end flex-shrink-0">
                <Button
                  v-tooltip.top="$t('WGPT_CUSTOM_DASHBOARDS.LIST.TOOLTIP.EDIT')"
                  icon="i-woot-edit-pen"
                  sm
                  slate
                  @click.stop="navigateToEdit(dashboard)"
                />
                <Button
                  v-tooltip.top="
                    $t('WGPT_CUSTOM_DASHBOARDS.LIST.TOOLTIP.DELETE')
                  "
                  icon="i-woot-bin"
                  sm
                  slate
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  @click.stop="deleteDialogRef.open(dashboard)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <DeleteCustomDashboardDialog ref="deleteDialogRef" />
  </div>
</template>
